import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpApplicationError } from 'errors/business-objects/application-error.http';
import { HTTPErrorCode } from 'errors/business-objects/http-error-codes';
import { CoreServerError } from 'errors/models/core-server-error';
import { DeviceProvider } from '../device/device';
import { EnvironmentProvider } from '../environment-variables/environment';
import { CoreHttpClientMode } from './core-http-client.mode';
import { IDownloadFileParams, IGetParams, IHttpResponse, IPostFileParams, IPostParams } from './core-http-client.models';
import { HttpResponseType } from './core-http-client.response-type';
import { CoreBrowserHttpEngine } from './core-http-engine.browser';
import { ICoreHttpEngine } from './core-http-engine.interface';
import { CoreNativeHttpEngine } from './core-http-engine.native';

/**
 * Interface for the CoreHttpClent
 */
@Injectable()
export class CoreHttpClient
{
    //#region Constructor

    /**
     * Class constructor
     */
    constructor
        (
            private device: DeviceProvider,
            private env: EnvironmentProvider
        )
    {

    }

    //#endregion


    //#region Properties

    /**
     * Array of request delays in seconds
     */
    private RequestDelays = [0, 1, 2, 6];


    /**
     * Core Http Enginge isntance
     */
    private HttpEngine: ICoreHttpEngine;

    //#endregion


    //#region Methods - Initialization
    /**
     * Initialize the provider
     * @param mode type of mode client to instantiate
     * @param httpClient Angular Http client instance
     * @param previewFile PreviewAnyFile instance
     * @returns {Promise<void>}
     */
    public async initializeProvider(mode: CoreHttpClientMode, httpClient: HttpClient): Promise<void>
    {
        switch (mode)
        {
            case CoreHttpClientMode.BROWSER: {
                // if browser load the engine using the angular 
                // http client instead
                this.HttpEngine = new CoreBrowserHttpEngine(httpClient);
                break;
            }
            case CoreHttpClientMode.NATIVE: {
                // if the device in question is an application
                // load the engine with the native plugin
                this.HttpEngine = new CoreNativeHttpEngine();
                break;
            }
            default: {
                throw new Error("Invalid Core Http Mode provided!");
            }
        }
    }

    //#endregion


    //#region Methods - HTTP verb methods

    /**
     * CoreHttpClient get method
     * @param {string} url destionation url
     * @param {HttpResponseType} responseType response type
     * @param {boolean} retryFailedRequests flag to retry request multiple times on each fail
     * @returns {Promise<T>}
     */
    public async get<T>(url: string, responseType: HttpResponseType = HttpResponseType.Json, retryFailedRequests: boolean = false): Promise<T>
    {
        // Declare the request attempt counter and the maximum retries
        let requestCounter = 0;
        let retryAttempts = retryFailedRequests ? this.RequestDelays.length : 1;

        while (requestCounter < retryAttempts)
        {
            try
            {
                // Prepare the request parameters
                let getParams: IGetParams = {
                    url: url,
                    responseType: responseType
                };

                // Perform the request
                let result = await this.HttpEngine.get<T>(getParams);

                // Resolve the promise
                return result.data;
            }
            catch (error)
            {
                // check if it is a genuine request error
                if ([HTTPErrorCode.HTTP_ERROR_HOST_NOT_FOUND_1, HTTPErrorCode.HTTP_ERROR_HOST_NOT_FOUND_2, HTTPErrorCode.HTTP_ERROR_NO_NETWORK].findIndex(val => val == error.status) > -1)
                {
                    // if it is a request error perform the request again
                    await this.wait(this.RequestDelays[requestCounter]);
                    // increment the retry attempts
                    requestCounter++;
                    // if requests fail ignore until all attempts have been made
                    if (requestCounter == retryAttempts)
                    {
                        throw this.createHttpErrorException(url, error);
                    }
                } else
                {
                    // if the error is not a request error
                    // then create createHttpErrorException
                    throw this.createHttpErrorException(url, error);
                }
            }
        }
    }

    /**
     * Post a file to the specified url
     * @param {string} url destination url
     * @param {string} name form file name
     * @param {File} value file 
     * @returns {Promise<T>}
     */
    public async postFile<T>(url: string, name: string, value: File | string): Promise<T>
    {
        // Prepare the request parameters
        let postFileParms: IPostFileParams = {
            url: url,
            name: name,
            value: value,
            responseType: HttpResponseType.Text
        }

        // Perform the request and return its result
        let result = await this.HttpEngine.postFile<T>(postFileParms);
        return result.data;
    }

    /**
     * CoreHttpClient Post Method
     * @param {string} url destination url
     * @param {any} body http request body
     * @param {HttpHeaders} headers optional http request headers 
     * @returns {Promise<T>}
     */
    public async post<T>(url: string, body: any, headers?: HttpHeaders): Promise<T>
    {
        try
        {
            // Prepare the request parameters
            let postParms: IPostParams = {
                url: url,
                body: body,
                headers: headers,
                responseType: HttpResponseType.Text
            };

            // Perform the request and return its result
            let result = await this.HttpEngine.post<T>(postParms);
            return result.data;
        }
        catch (error)
        {
            let exception = this.createHttpErrorException(url, error);
            throw exception;
        }
    }

    /**
     * Wait function
     * @param {number} seconds duration in seconds to wait
     */
    private wait(seconds: number)
    {
        return new Promise(resolve => setTimeout(resolve, seconds * 1000));
    }

    /**
     * Download a file
     * @param fileName name of the file to be saved 
     * @param url file download url
     */
    public async downloadFile(url: string): Promise<any>
    {
        try
        {
            // Prepare the request parameters
            let requestParams: IDownloadFileParams = {
                url: url
            };

            // Perform the request and await the result
            await this.HttpEngine.downloadFile(requestParams);
        }
        catch (error)
        {
            throw this.createHttpErrorException(url, error);
        }
    }

    /**
       * Get a CoreHttpErrorResponse object from the error received
       * @param url
       * @param error
       */
    protected createHttpErrorException(url: string, error: IHttpResponse<any>): HttpApplicationError
    {
        let message: string;
        let coreServerError: CoreServerError;

        console.log(`error is : `, error);

        // get the core server error information, if any
        // STATUS 0 / 1 - Could not resolve host, may be because of no internet connection
        switch (error.status)
        {
            case (HTTPErrorCode.HTTP_ERROR_HOST_NOT_FOUND_1):
            case (HTTPErrorCode.HTTP_ERROR_HOST_NOT_FOUND_2):
            case (HTTPErrorCode.HTTP_ERROR_NOT_FOUND):
                {
                    message = `Could not communicate with the server`;
                    break;
                }
            case (HTTPErrorCode.HTTP_ERROR_OUT_OF_SESSION): {
                message = `Client out of session`;
                break;
            }
            case (HTTPErrorCode.HTTP_ERROR_SERVER_ERROR): {
                coreServerError = error.data;
                message = `Internal server error`;
                break;
            }
            default: {
                message = `Could not communicate with the server`;
                break;
            }
        }

        return new HttpApplicationError(message, this.device.getDeviceType(), this.env.appVersion, this.device.getDeviceSoftwareVersion(), this.device.getDeviceUserAgent(), error.status, url, coreServerError);
    }

    //#endregion


    //#region Methods - Utilities

    /**
     * Check if the object returned is an error response
     * @param object
     */
    public checkIfError(object: any): boolean
    {
        return 'error' in object;
    }

    /**
     * Clear the underlying cookies
     */
    public clearCookies(): void
    {
        this.HttpEngine.clearCookies();
    }

    //#endregion
}